import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import careTeamApi, { errorText } from './careTeamApi';
import PersonPicker from './PersonPicker';
import { plural } from './careTeamText';

const UL = { paddingLeft: '1.25rem' };
const NOT_FROM = 'Not assigned to this person in this slot';
const TITLES = { assign: 'Assign nurse', remove: 'Remove nurse', move: 'Move all patients' };
const SLOT_CHOICES = [
    { value: 'rpm', label: 'RPM' },
    { value: 'ccm', label: 'CCM' },
    { value: 'both', label: 'Both' },
];

/**
 * Bulk care-team change with a server preview before anything is written.
 * mode assign: choose nurse (unless fixedPerson) + slot; remove: choose the nurse to take off + slot;
 * move: fromPerson's patients go to the chosen nurse.
 */
const BulkCareTeamDialog = ({ show, mode = 'assign', patientIds = [], fixedPerson, fromPerson, defaultSlot = 'rpm', source = 'bulk', onHide, onDone }) => {
    const [nurses, setNurses] = useState([]);
    const [personId, setPersonId] = useState('');
    const [slot, setSlot] = useState(defaultSlot);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show) return;
        setPreview(null);
        setResult(null);
        setError('');
        setSlot(mode === 'move' ? 'both' : defaultSlot);
        setPersonId(mode === 'remove' && fromPerson ? fromPerson.id : '');
        if (fixedPerson) return;
        // Assign/move: only nurses of the selected patients' organization(s); remove: any nurse currently assigned.
        const load = mode === 'remove' || !patientIds.length ? careTeamApi.people('nurse') : careTeamApi.peopleFor('nurse', patientIds);
        load.then(setNurses).catch((e) => setError(errorText(e)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, mode, defaultSlot, fixedPerson, fromPerson, patientIds.join(',')]);

    const body = () => {
        if (mode === 'remove') return { patientIds, slot, personId: null, fromPersonId: personId, source };
        if (mode === 'move') return { patientIds, slot, personId, fromPersonId: fromPerson.id, source: 'move' };
        return { patientIds, slot, personId: fixedPerson ? fixedPerson.id : personId, source };
    };
    const chosen = fixedPerson ? fixedPerson.id : personId;

    const doPreview = async () => {
        setBusy(true);
        setError('');
        try {
            setPreview(await careTeamApi.preview(body()));
        } catch (e) {
            setError(errorText(e));
        } finally {
            setBusy(false);
        }
    };
    const doApply = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await careTeamApi.apply({ ...body(), fingerprint: preview.fingerprint });
            setResult(res);
            if (onDone) onDone(res);
        } catch (e) {
            setError(errorText(e));
            if (e?.response?.status === 409) setPreview(null);
        } finally {
            setBusy(false);
        }
    };

    const s = preview?.summary;
    const skipped = (preview?.items || []).filter(
        (i) => (i.status === 'skipped' || i.status === 'error') && !(mode === 'move' && i.reason === NOT_FROM)
    );
    const personName = preview?.person?.name || fixedPerson?.name || fromPerson?.name;

    return (
        <Modal show={show} onHide={onHide} size="lg" aria-labelledby="bulk-care-team-title">
            <Modal.Header closeButton>
                <Modal.Title id="bulk-care-team-title" as="h6">
                    {TITLES[mode]}{fixedPerson ? ` — ${fixedPerson.name}` : ''}{mode === 'move' && fromPerson ? ` of ${fromPerson.name}` : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: '14px' }}>
                <p className="mb-2"><strong>{plural(patientIds.length, 'patient')}</strong> selected.</p>
                {!result && !preview && (
                    <>
                        {!fixedPerson && (
                            <div className="mb-2">
                                <label htmlFor="bulk-care-team-nurse" className="d-block mb-1">
                                    {mode === 'remove' ? 'Nurse to remove' : mode === 'move' ? 'Move to nurse' : 'Nurse'}
                                </label>
                                <PersonPicker
                                    id="bulk-care-team-nurse"
                                    label="Nurse"
                                    people={nurses}
                                    placeholder="Choose nurse…"
                                    value={personId}
                                    exclude={mode === 'move' && fromPerson ? [fromPerson.id] : []}
                                    onChange={setPersonId}
                                />
                            </div>
                        )}
                        <div role="radiogroup" aria-label="Slot" className="mb-2">
                            <span className="mr-2">Slot:</span>
                            {SLOT_CHOICES.map((c) => (
                                <label key={c.value} className="mr-3">
                                    <input type="radio" name="bulk-care-team-slot" value={c.value} checked={slot === c.value} onChange={() => setSlot(c.value)} /> {c.label}
                                </label>
                            ))}
                        </div>
                    </>
                )}
                {preview && !result && (
                    <div data-testid="bulk-care-team-preview">
                        <p className="mb-1">
                            {mode === 'remove' ? `Remove ${personName}` : mode === 'move' ? `Move to ${personName}` : `Assign ${personName}`} as{' '}
                            {slot === 'both' ? 'RPM and CCM nurse' : `${slot.toUpperCase()} nurse`}:
                        </p>
                        <ul className="mb-2" style={UL}>
                            <li>{plural(s.patients, 'patient')}</li>
                            {mode === 'remove' ? (
                                <li><strong>{plural(s.remove, 'slot')}</strong> will be cleared</li>
                            ) : (
                                <>
                                    <li>{plural(s.assign, 'empty slot')} will be filled</li>
                                    <li>
                                        <strong>{s.replace}</strong> will REPLACE an existing nurse
                                        {preview.replacements.length > 0 && (
                                            <ul data-testid="bulk-care-team-replacements" style={UL}>
                                                {preview.replacements.map((r) => (
                                                    <li key={`${r.patientId}-${r.slot}`}>{r.patientName} — {r.slotLabel}: {r.fromName}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                    <li>{s.alreadyAssigned} already assigned to {personName}</li>
                                </>
                            )}
                            {skipped.length > 0 && (
                                <li>
                                    {plural(skipped.length, 'slot')} skipped
                                    <ul style={UL}>
                                        {skipped.slice(0, 20).map((i) => (
                                            <li key={`${i.patientId}-${i.slot}`}>{i.patientName || 'Unknown patient'}{i.slotLabel ? ` (${i.slotLabel})` : ''}: {i.reason}</li>
                                        ))}
                                        {skipped.length > 20 && <li>…and {skipped.length - 20} more</li>}
                                    </ul>
                                </li>
                            )}
                        </ul>
                        {s.changes === 0 && <p className="text-muted mb-0">Nothing to change.</p>}
                    </div>
                )}
                {result && (
                    <div data-testid="bulk-care-team-result">
                        <p className="mb-1"><strong>Saved.</strong> {plural(result.summary.changed, 'patient')} updated.</p>
                        {result.summary.conflict + result.summary.error > 0 && (
                            <ul style={UL}>
                                {result.results.filter((r) => r.status !== 'ok').map((r) => (
                                    <li key={r.patientId}>{r.patientName}: {r.reason}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {error && <div className="alert alert-danger py-1 mt-2 mb-0">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                {result ? (
                    <button type="button" className="btn btn-sm btn-primary" onClick={onHide}>Close</button>
                ) : (
                    <>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={preview ? () => setPreview(null) : onHide}>
                            {preview ? 'Back' : 'Cancel'}
                        </button>
                        {preview ? (
                            <button type="button" className={`btn btn-sm ${mode === 'remove' ? 'btn-danger' : 'btn-primary'}`} disabled={busy || s.changes === 0} onClick={doApply}>
                                Confirm
                            </button>
                        ) : (
                            <button type="button" className="btn btn-sm btn-primary" disabled={busy || !chosen || !patientIds.length} onClick={doPreview}>
                                Preview
                            </button>
                        )}
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default BulkCareTeamDialog;
